import { useRouteMatcher, useRoutes, useUrlGenerator } from '@folklore/routes';
import { faClose, faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';
import { useCallback, useMemo, useState } from 'react';

import { useCurrentDocument } from '../../hooks/useDocument';

import { useApi } from '../../contexts/ApiContext';
import BasicButton from '../buttons/BasicButton';
import Menu from './Menu';

import styles from '../../styles/menus/admin-menu.module.css';

interface AdminMenuProps {
    className?: string;
}

function AdminMenu({ className }: AdminMenuProps) {
    const routes = useRoutes();
    const url = useUrlGenerator();
    const routeMatcher = useRouteMatcher();
    const [opened, setOpened] = useState(false);
    const onClickOpen = useCallback(() => {
        setOpened(!opened);
    }, [opened]);
    const api = useApi();
    const { baseUrl: apiBaseUrl = null } = api;
    const currentDocument = useCurrentDocument();
    const { type: documentType = null } = currentDocument || {};

    const items = useMemo(
        () =>
            [
                {
                    label: 'Partage',
                    route: routes.document,
                    href: ({ type, slug }) => url('document.share', { type, slug }),
                },
                {
                    label: 'Modifier',
                    route: routes.document,
                    href: (params, { identifier = '' }) =>
                        `https://wordpress.urbania.ca/wp-admin/post.php?post=${identifier.replace(/^wordpress-/, '')}&action=edit`,
                    external: true,
                },
                {
                    label: 'Exporter',
                    route: routes.contest,
                    href: (params, { id }) => `${apiBaseUrl}/contests/${id}.csv`,
                    external: true,
                },
                {
                    label: 'Exporter',
                    route: routes.survey,
                    href: (params, { form: { id: formId } = {} }) =>
                        `${apiBaseUrl}/forms/${formId}.csv`,
                    external: true,
                },
            ]
                .map(({ route = null, href, ...item }) => {
                    const [match = false, params] =
                        route !== null ? routeMatcher(route) : [true, {}];
                    if (!match) {
                        return null;
                    }
                    return {
                        ...item,
                        href: isFunction(href) ? href(params, currentDocument || {}) : href,
                    };
                })
                .filter((item) => item !== null),
        [routeMatcher, routes, apiBaseUrl, currentDocument],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.transparent]:
                        documentType === 'video' ||
                        documentType === 'podcast' ||
                        documentType === 'micromag',
                    [styles.hidden]: items.length === 0,
                    [styles.opened]: opened,
                },
                className,
            ])}
        >
            <BasicButton className={styles.openButton} onClick={onClickOpen}>
                <FontAwesomeIcon icon={opened ? faClose : faCog} className={styles.icon} />
            </BasicButton>
            <Menu
                items={items}
                className={styles.menu}
                itemsClassName={styles.items}
                itemClassName={styles.item}
                buttonClassName={styles.button}
            />
        </div>
    );
}

export default AdminMenu;
